// src/pages/Users/Adherent/DiplomePage.jsx

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";

import useScrollToTop from "./../../../hooks/useScollToTop";
import { getSettings } from "../../../services/settingsServices";

import "./DiplomePage.css";

const DIPLOME_BG = "/img/diplome/fond-diplome-judo-a4.jpg";
const DEFAULT_LOGO = "/img/logo_club_default.png";

const formatDateFR = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return String(date);

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const toAbsolute = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${value}`;
};

const getBeltImage = (beltImage) => {
  if (!beltImage) return "";
  if (String(beltImage).startsWith("/")) return beltImage;
  return `/img/belt/${beltImage}`;
};

export default function DiplomePage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();

  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const {
    adherentNom,
    adherentPrenom,
    dateNaissance,
    datePassage,
    beltImage,
    beltName,
  } = location.state || {};

  React.useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoading(true);

        const data = await getSettings();
        if (!mounted) return;

        setSettings(Array.isArray(data) && data.length > 0 ? data[0] : null);
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
        if (mounted) setSettings(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  if (!location.state) {
    return (
      <main className="diploma-page">
        <div className="diploma-error">
          Pas de données disponibles pour ce diplôme.
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="diploma-page">
        <div className="diploma-error">Chargement du diplôme...</div>
      </main>
    );
  }

  const clubName = settings?.nom_club || "Club de Judo";
  const clubCity = settings?.commune_club || settings?.ville_club || "—";
  const clubAddress = settings?.adresse_club || "";
  const clubWebsite = settings?.siteweb_club || "";
  const clubLogo = toAbsolute(settings?.logo_club) || DEFAULT_LOGO;

  const fullName = `${adherentPrenom || ""} ${String(
    adherentNom || "",
  ).toUpperCase()}`.trim();

  const beltSrc = getBeltImage(beltImage);

  const handlePrint = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.print();
      }, 150);
    });
  };

  return (
    <main className="diploma-page">
      <div className="diploma-toolbar no-print pt-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="diploma-toolbar-button diploma-toolbar-button-secondary"
        >
          <FaArrowLeft />
          Retour
        </button>

        <button
          type="button"
          onClick={handlePrint}
          className="diploma-toolbar-button diploma-toolbar-button-primary"
        >
          <FaPrint />
          Imprimer / Générer PDF
        </button>
      </div>

      <section
        className="diploma-sheet"
        style={{
          "--diploma-bg": `url("${DIPLOME_BG}")`,
        }}
      >
        <div className="diploma-background-overlay" />

        <header className="diploma-header">
          <div className="diploma-kanji-block">
            <img
              src="/img/diplome/kanji_judo2.png"
              alt="Judo"
              className="diploma-kanji"
            />
          </div>

          <div className="diploma-title-zone">
            <p className="diploma-overline">{clubName}</p>
            <h1>Attestation de Grade</h1>
            <p className="diploma-subtitle">
              Décernée selon les valeurs du code moral du Judo
            </p>
          </div>

          <div className="diploma-kano-block">
            <img
              src="/img/diplome/jigoro_kano_c7tpyj.jpg"
              alt="Jigoro Kano"
              className="diploma-kano"
            />
          </div>
        </header>

        <div className="diploma-main">
          <aside className="diploma-left-panel">
            <div className="diploma-code-card">
              <img
                src="/img/diplome/code_moral_judo_ai5yvf.jpg"
                alt="Code moral du Judo"
                className="diploma-code-moral"
              />
            </div>
          </aside>

          <section className="diploma-content">
            <div className="diploma-belt-zone">
              {beltSrc ? (
                <img
                  src={beltSrc}
                  alt={`Ceinture ${beltName || ""}`}
                  className="diploma-belt"
                />
              ) : (
                <div className="diploma-belt-placeholder">
                  Ceinture {beltName || "—"}
                </div>
              )}
            </div>

            <div className="diploma-recipient">
              <p className="diploma-small-label">Ceinture</p>
              <h2>{beltName || "—"}</h2>

              <p className="diploma-small-label diploma-margin-top">
                Décernée à
              </p>
              <h3>{fullName || "—"}</h3>

              <div className="diploma-grid-info">
                <div>
                  <span>Né(e) le</span>
                  <strong>{formatDateFR(dateNaissance)}</strong>
                </div>

                <div>
                  <span>Délivrée à</span>
                  <strong>{clubCity}</strong>
                </div>

                <div>
                  <span>Date de passage</span>
                  <strong>{formatDateFR(datePassage)}</strong>
                </div>
              </div>

              <p className="diploma-text">
                A satisfait aux épreuves de son grade et se comporte selon les
                règles de l’esprit du Judo, dans le respect du code moral.
              </p>
            </div>

            <div className="diploma-signatures">
              <div className="diploma-signature">
                <div className="diploma-signature-line" />
                <p>Professeur</p>
              </div>

              <div className="diploma-signature">
                <div className="diploma-signature-line" />
                <p>Président</p>
              </div>
            </div>
          </section>
        </div>

        <footer className="diploma-footer">
          <div className="diploma-club-logo-wrap">
            <img src={clubLogo} alt={clubName} className="diploma-club-logo" />
          </div>

          <div className="diploma-footer-center">
            <p className="diploma-footer-quote">
              Entraide et prospérité mutuelle
            </p>
            <p className="diploma-footer-japanese">自他共栄</p>
          </div>

          <div className="diploma-club-info">
            <strong>{clubName}</strong>
            {clubAddress && <span>{clubAddress}</span>}
            {clubWebsite && <span>{clubWebsite}</span>}
          </div>
        </footer>
      </section>
    </main>
  );
}
